import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { PresetHoursProvider } from './PresetHoursContext';
import { PositionsProvider } from './PositionsContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box, useMediaQuery, useTheme } from '@mui/material';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { queryClient } from './lib/react-query';
import { ClerkUserProvider } from './providers/ClerkUserProvider';
import { OrganizationProvider } from './providers/OrganizationProvider';
import Sidebar, { DRAWER_WIDTH } from './Sidebar';
import Staff from './Staff';
import Schedule from './Schedule';
import Schedule2 from './Schedule2';
import Schedule3 from './Schedule3';
import Salaries from './Salaries';
// Removed History import
import Dashboard from './Dashboard';
import Revenue from './Revenue';
import Expenses from './Expenses';
import SupplierDetail from './SupplierDetail';
import FinancialOverview from './FinancialOverview';

// Auth pages
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';

// Organization pages
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import AcceptInvitePage from './pages/AcceptInvitePage';

// Public pages
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';

// ...existing code...
// ...existing code...
import UnifiedSettings from './UnifiedSettings';
import PaymentManagementV2 from './PaymentManagementV2';
import TestReactQuery from './TestReactQuery';
import StoreHoursSettings from './settings/StoreHoursSettings';
import StaffingRequirementsSettings from './settings/StaffingRequirementsSettings';
import PositionsSettingsPage from './settings/PositionsSettings';
import PresetHoursSettingsPage from './settings/PresetHoursSettings';
import MyDataSettings from './settings/MyDataSettings';
import CategoriesSettings from './settings/CategoriesSettings';
import BillingSettingsPage from './settings/BillingSettingsPage';
import AiAssistant from './components/AiAssistant';
import PlanComplianceGate from './components/PlanComplianceGate';

function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <PlanComplianceGate>
    <Box sx={{ display: 'flex', backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Sidebar />
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: isMobile ? 0 : `${DRAWER_WIDTH}px`,
          mt: isMobile ? '56px' : 0,
          p: { xs: 2, sm: 3, md: 4 },
          minHeight: '100vh',
          width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
          overflowX: 'hidden',
        }}
      >
        <Routes>

          <Route path="/" element={<Dashboard />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/schedule2" element={<Schedule2 />} />
          <Route path="/schedule3" element={<Schedule3 />} />
          <Route path="/salaries" element={<Salaries />} />
          <Route path="/payments" element={<PaymentManagementV2 />} />
          <Route path="/payments-v2" element={<Navigate to="/payments" replace />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/revenue" element={<Revenue />} />
          <Route path="/financial-overview" element={<FinancialOverview />} />
          <Route path="/suppliers/:id" element={<SupplierDetail />} />
          <Route path="/settings" element={<Navigate to="/settings/positions" replace />} />
          <Route path="/settings/store-hours" element={<StoreHoursSettings />} />
          <Route path="/settings/staffing-requirements" element={<StaffingRequirementsSettings />} />
          <Route path="/settings/positions" element={<PositionsSettingsPage />} />
          <Route path="/settings/preset-hours" element={<PresetHoursSettingsPage />} />
          <Route path="/settings/mydata" element={<MyDataSettings />} />
          <Route path="/settings/categories" element={<CategoriesSettings />} />
          <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
          <Route path="/settings/billing" element={<BillingSettingsPage />} />
          <Route path="/test-react-query" element={<TestReactQuery />} />
        </Routes>
      </Box>
      
      {/* AI Assistant Chat Widget */}
      <AiAssistant />
    </Box>
    </PlanComplianceGate>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <CssBaseline />
        <Routes>
          {/* Public pages */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          
          {/* Public auth routes */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          
          {/* Invite page - special handling (can be accessed signed-in or signed-out) */}
          <Route path="/invite/:token" element={
            <ClerkUserProvider>
              <AcceptInvitePage />
            </ClerkUserProvider>
          } />
          
          {/* Protected routes */}
          <Route path="/*" element={
            <>
              <SignedIn>
                <ClerkUserProvider>
                  <OrganizationProvider>
                    <PresetHoursProvider>
                      <PositionsProvider>
                        <AppContent />
                      </PositionsProvider>
                    </PresetHoursProvider>
                  </OrganizationProvider>
                </ClerkUserProvider>
              </SignedIn>
              <SignedOut>
                <LandingPage />
              </SignedOut>
            </>
          } />
        </Routes>
      </Router>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
